import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import { Condition, isRule, Rule } from '@dadohr/shared/domainTypes/rules';
import styled from '@emotion/styled';
import { GroupIcon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormattedRuleElement, RuleBuilderConfig } from './types';
import {
  formatCondition,
  formatRule,
  isExpandedDateRule,
  isExpandedStringRule
} from './utils';

type RuleRendererProps<T extends string> = {
  value: Rule<T> | null;
  config: RuleBuilderConfig<T>;
  variant?: 'oneline' | 'multiline' | 'inline';
};

const ConditionBox = styled('div')`
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(2)}px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: ${(p) => p.theme.custom.colors.gray.w100};
  margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
`;

const GroupContainer = styled('div')`
  margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
`;

const GroupBox = styled('div')`
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(2)}px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: ${(p) => p.theme.custom.colors.gray.w100};
  margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
`;

const GroupHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(0.5)}px;
  margin-bottom: ${(p) => p.theme.spacing(1)}px;
  font-size: 14px;
`;

const ConditionalText = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: ${(p) => p.theme.spacing(0.5)}px 0;
  font-size: 14px;
  font-weight: bold;
  color: ${(p) => p.theme.palette.text.primary};
`;

const NestedConditionBox = styled('div')`
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(2)}px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: white;
  margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
`;

const FormattedGroup = styled('div')<{ first: boolean }>`
  ${(p) => (p.first ? `margin-left: ${p.theme.spacing(2)}px` : '')};
`;

/**
 * Full renderer in multiline with rule grouping
 */
const renderMultilineRule = <T extends string>(
  rule: Rule<T>,
  config: RuleBuilderConfig<T>,
  t: any,
  isRoot = true
): React.ReactNode => {
  const elements: React.ReactNode[] = [];

  rule.els.forEach((el, index) => {
    const isLastElement = index === rule.els.length - 1;

    if (isRule(el)) {
      // Check if this is an expanded string or date rule that should be collapsed
      const expandedCheck = isExpandedStringRule(el, config);
      const expandedDateCheck = isExpandedDateRule(el, config);

      if (
        (expandedCheck.isExpanded && expandedCheck.key && expandedCheck.operator && expandedCheck.values) ||
        (expandedDateCheck.isExpanded && expandedDateCheck.key && expandedDateCheck.value !== undefined)
      ) {
        // Render as a single condition in appropriate box
        const condition = (
          expandedDateCheck.isExpanded
            ? {
                k: expandedDateCheck.key as T,
                op: expandedDateCheck.operator as any,
                v: expandedDateCheck.value as any
              }
            : {
                k: expandedCheck.key as T,
                op: expandedCheck.operator as any,
                v: expandedCheck.values as any
              }
        ) as Condition<T>;

        const conditionElement = formatCondition(condition, config, t);
        
        if (isRoot) {
          elements.push(
            <ConditionBox key={index}>
              {conditionElement}
            </ConditionBox>
          );
        } else {
          elements.push(
            <NestedConditionBox key={index}>
              {conditionElement}
            </NestedConditionBox>
          );
        }
      } else {
        // Render as a nested group
        const content = (
          <div key={index}>
            <GroupHeader>
              <GroupIcon size={20} />
              Filter group
            </GroupHeader>
            {renderMultilineRule(el, config, t, false)}
          </div>
        );

        if (isRoot) {
          elements.push(
            <GroupContainer key={index}>
              <GroupBox>{content}</GroupBox>
            </GroupContainer>
          );
        } else {
          elements.push(
            <GroupBox key={index}>{content}</GroupBox>
          );
        }
      }
    } else {
      // Single condition - render in appropriate box
      const conditionElement = formatCondition(el, config, t);
      
      if (isRoot) {
        elements.push(
          <ConditionBox key={index}>
            {conditionElement}
          </ConditionBox>
        );
      } else {
        elements.push(
          <NestedConditionBox key={index}>
            {conditionElement}
          </NestedConditionBox>
        );
      }
    }

    // Add conditional separator between elements
    if (!isLastElement && rule.els.length > 1) {
      elements.push(
        <ConditionalText key={`sep-${index}`}>
          {rule.type === 'all' ? 'AND' : 'OR'}
        </ConditionalText>
      );
    }
  });

  return <>{elements}</>;
};

/**
 * Single line renderer is unchanged
 */
const renderOneliner = (v: FormattedRuleElement, inline: boolean) => {
  const els: React.ReactNode[] = [];
  const addEls = (el: FormattedRuleElement, parenthesis: boolean) => {
    if (parenthesis) {
      els.push(<span key={`open-${els.length}`}>(</span>);
    }
    el.els.forEach((x, i) => {
      if (typeof x === 'string') {
        els.push(<span key={`text-${els.length}`}>{x}</span>);
      } else {
        addEls(x, true);
      }
      if (i !== el.els.length - 1) {
        els.push(<strong key={`sep-${els.length}`}>{el.type === 'all' ? ' and ' : ' or '}</strong>);
      }
    });
    if (parenthesis) {
      els.push(<span key={`close-${els.length}`}>)</span>);
    }
  };

  addEls(v, false);

  return inline ? (
    <span>
      {els.map((el, i) => (
        <React.Fragment key={i}>{el}</React.Fragment>
      ))}
    </span>
  ) : (
    <div>
      {els.map((el, i) => (
        <React.Fragment key={i}>{el}</React.Fragment>
      ))}
    </div>
  );
};

/**
 * Legacy renderer for rule groups
 */
const renderMultilinerLegacy = (v: FormattedRuleElement, layer = 0) => {
  return (
    <FormattedGroup first={layer !== 0} key={layer}>
      {v.els.map((el, i) => (
        <React.Fragment key={`${layer}-${i}`}>
          {typeof el === 'string' ? (
            <div>{el}</div>
          ) : (
            <div>{renderMultilinerLegacy(el, layer)}</div>
          )}
          {i !== v.els.length - 1 && (
            <div>
              <strong>{v.type === 'all' ? ' and ' : ' or '}</strong>
            </div>
          )}
        </React.Fragment>
      ))}
    </FormattedGroup>
  );
};


export const RuleRenderer = <T extends string>({
  value,
  config,
  variant = 'oneline'
}: RuleRendererProps<T>) => {
  const { t } = useTranslation();
  
  if (!value) {
    return null;
  }

  const inline = variant === 'inline';
  
  if (variant === 'oneline' || inline) {
    const v = formatRule(value, config, t);
    return renderOneliner(v, inline);
  }

  if (variant === 'multiline') {
    return <div>{renderMultilineRule(value, config, t, true)}</div>;
  }

  return assertNever(variant);
};